/**
 * useAuditIssues Hook
 *
 * Computes derived values from audit data.
 * These are NOT stored in Redux - they're computed on-demand from RTK Query data.
 */

import { useMemo } from 'react';

import type { RuleComplianceAssessment } from '../types';

export interface CptAnalysisData {
  cptAnalysisPerformed: boolean;
  cptMatch: boolean;
  providerCptCodes: string[] | null;
  recommendedCptCodes: string[] | null;
  targetCptCodes: string[] | null;
  targetCptSource: 'user' | 'ai' | null;
}

interface AuditIssuesResult {
  /** Total count of non-compliant rules (excludes warnings) */
  totalIssueCount: number;
  /** Map of section_id to issue count for that section */
  sectionIssueMap: Record<string, number>;
  /** Get issue count for a specific section */
  getSectionIssueCount: (sectionId: string) => number;
}

/**
 * Computes audit issue counts from rule compliance assessments
 * Only counts non-compliant rules (excludes warnings)
 */
export function useAuditIssues(
  ruleComplianceAssessments: RuleComplianceAssessment[] | null | undefined,
): AuditIssuesResult {
  const sectionIssueMap = useMemo(() => {
    if (!ruleComplianceAssessments) return {};

    const map: Record<string, number> = {};

    ruleComplianceAssessments
      .filter(r => r.compliant === 'no')
      .forEach(rule => {
        rule.relevant_sections?.forEach(sectionId => {
          map[sectionId] = (map[sectionId] || 0) + 1;
        });
      });

    return map;
  }, [ruleComplianceAssessments]);

  const totalIssueCount = useMemo(() => {
    if (!ruleComplianceAssessments) return 0;
    return ruleComplianceAssessments.filter(r => r.compliant === 'no').length;
  }, [ruleComplianceAssessments]);

  const getSectionIssueCount = useMemo(
    () => (sectionId: string) => sectionIssueMap[sectionId] || 0,
    [sectionIssueMap],
  );

  return {
    totalIssueCount,
    sectionIssueMap,
    getSectionIssueCount,
  };
}

/**
 * Extracts CPT analysis data from note audit detail
 * 
 * Now uses the new target_cpt_codes and target_cpt_source fields to determine visibility.
 * The CPT section should show if target_cpt_codes has values, regardless of source.
 */
export function useCptAnalysis(
  currentRevision:
    | {
        cpt_analysis_performed?: boolean;
        cpt_match?: boolean;
        provider_cpt_codes?: string[] | null;
        recommended_cpt_codes?: string[] | null;
        target_cpt_codes?: string[] | null;
        target_cpt_source?: string | null;
      }
    | null
    | undefined,
): CptAnalysisData | null {
  return useMemo(() => {
    if (!currentRevision) return null;

    const targetCptCodes = currentRevision.target_cpt_codes ?? null;
    const targetCptSource = currentRevision.target_cpt_source as 'user' | 'ai' | null;
    
    // CPT analysis is considered "performed" if we have target_cpt_codes
    // (new field) OR the old cpt_analysis_performed flag is set
    const hasCptData = 
      (targetCptCodes && targetCptCodes.length > 0) ||
      currentRevision.cpt_analysis_performed;

    return {
      cptAnalysisPerformed: hasCptData ?? false,
      cptMatch: currentRevision.cpt_match ?? false,
      providerCptCodes: currentRevision.provider_cpt_codes ?? null,
      recommendedCptCodes: currentRevision.recommended_cpt_codes ?? null,
      targetCptCodes,
      targetCptSource,
    };
  }, [currentRevision]);
}
